import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';

export const WristCamera = zod.object({
  kind: zod.literal('wristCamera'),
  name: zod.string().default('Wrist Camera'),
  // realsense defaults
  brightness: zod.number().default(0),
  contrast: zod.number().default(50),
  exposure: zod.number().default(350),
  sharpness: zod.number().default(50),
  hue: zod.number().default(0),
  whiteBalance: zod.number().default(4600),
  autoWhiteBalance: zod.boolean().default(true),
});

export type WristCamera = zod.infer<typeof WristCamera>;

export const CameraIntegration = zod.union([
  zod.never(), // remove when we have > 1 camera type; it's only here to make a valid union
  WristCamera,
]);

export type CameraIntegration = zod.infer<typeof CameraIntegration>;

export const CameraStatus = zod.object({
  connected: zod.boolean(),
  message: zod.string(),
});

export type CameraStatus = zod.infer<typeof CameraStatus>;

export const CameraIntrinsics = zod.object({
  width: zod.number(), // pixels
  height: zod.number(), // pixels
  // Focal length
  fx: zod.number(),
  fy: zod.number(),
  // Principal point
  ppx: zod.number(),
  ppy: zod.number(),
  error: zod.number().optional(),
});

export type CameraIntrinsics = zod.infer<typeof CameraIntrinsics>;

export const CameraIntrinsicsExtended = CameraIntrinsics.extend({
  distortionCoefficients: zod.array(zod.number()).default([]),
  distortionModel: zod.string().nullable().default(null),
});

export type CameraIntrinsicsExtended = zod.infer<
  typeof CameraIntrinsicsExtended
>;

export const CameraStreamSource = zod.object({
  id: zod.string(),
  name: zod.string(),
});

const BaseChessboardParameters = zod.object({
  rows: zod.number().gt(0),
  cols: zod.number().gt(0),
  squareSize: zod.number().gt(0), // specified in meters
});

const ChessboardSimple = BaseChessboardParameters.extend({
  kind: zod.literal('chessboard'),
});

const ChessboardCharuco = BaseChessboardParameters.extend({
  kind: zod.literal('charuco'),
  arucoDict: zod.string(),
  markerSize: zod.number().gt(0),
});

export const ChessboardParameters = zod.discriminatedUnion('kind', [
  ChessboardSimple,
  ChessboardCharuco,
]);

export type ChessboardParameters = zod.infer<typeof ChessboardParameters>;

/**
 * Default chessboard parameters: the 5-square × 7-square calibration board
 * used by current robots (4 inner-corner rows × 6 inner-corner columns).
 *
 * `rows` / `cols` here match the charuco-side constants
 * (`CHARUCO_ROWS=4, CHARUCO_COLS=6`) in
 * `python/apps/vision-bot/src/vision/camera_calibration.py`, where
 * `rows` indexes the board's horizontal axis and `cols` the vertical axis.
 * The plane-creation path (`get_camera_chessboard_transform`) interprets
 * these values under that convention; other callers of vision-bot's
 * standard-chessboard helpers use the legacy "cols = horizontal" convention.
 */
export const DEFAULT_CHESSBOARD_PARAMS: ChessboardParameters = {
  cols: 6,
  rows: 4,
  squareSize: 0.03, // specified in meters
  kind: 'chessboard',
};

/**
 * The production ChArUco calibration board, used as the default for the
 * plane-from-camera capture flow. These values mirror vision-bot's
 * `CHARUCO_*` constants (`python/apps/vision-bot/src/vision/camera_calibration.py`):
 * 4×6 inner corners (5×7 squares), 34.26 mm squares, 27.5 mm markers,
 * `DICT_6X6_250`. The mujoco sim board
 * (`python/apps/mujoco-bot/scenes/default/textures/generate_charuco.py`) is
 * generated to match these exact values so the same capture path works in
 * sim and on hardware. Sending the real board spec (rather than a generic
 * `kind: 'chessboard'`) makes vision-bot run ChArUco detection — which uses
 * the markers to disambiguate board orientation — instead of falling back to
 * standard-chessboard detection at the wrong scale.
 */
export const DEFAULT_CHARUCO_BOARD_PARAMS: ChessboardParameters = {
  cols: 6,
  rows: 4,
  squareSize: 0.03426, // meters
  kind: 'charuco',
  arucoDict: 'DICT_6X6_250',
  markerSize: 0.0275, // meters
};

export const ExtrinsicsType = zod.enum(['base', 'tcp']);

export type ExtrinsicsType = zod.infer<typeof ExtrinsicsType>;

export type CameraStreamSource = zod.infer<typeof CameraStreamSource>;

export const CalibrationEntry = zod.object({
  wristPose: CartesianPose,
  storageID: zod.string(), // Empty string used to indicate image not captured yet
  timestamp: zod.union([
    zod.date(),
    zod.string().transform((str) => new Date(str)),
  ]),
});

const CalibrationDataEntryBase = zod.object({
  id: zod.string(),
  timestamp: zod.union([
    zod.date(),
    zod.string().transform((str) => new Date(str)),
  ]),
  name: zod.string(),
  cameraId: zod.string(),
  streamId: zod.string().nullable().default(null),
  data: CalibrationEntry.array().default([]),
  isBuiltIn: zod.boolean().default(false),
  chessboard: ChessboardParameters.default(DEFAULT_CHESSBOARD_PARAMS),
});

export const IntrinsicsCalibrationDataEntry = CalibrationDataEntryBase.extend({
  intrinsics: CameraIntrinsicsExtended.nullable().default(null),
});

export type IntrinsicsCalibrationDataEntry = zod.infer<
  typeof IntrinsicsCalibrationDataEntry
>;

export const ExtrinsicsCalibrationDataEntry = CalibrationDataEntryBase.extend({
  intrinsicsId: zod.string().nullable().default(null),
  extrinsics: CartesianPose.nullable().default(null),
  kind: ExtrinsicsType,
  // Body the TCP extrinsics pose is parented to (e.g. an MJCF body name
  // like 'wrist_3_link'). Optional metadata used by self-reporting cameras
  // (currently mujoco-bot virtual cameras seeded via the feathers-bot
  // equipment hook). Null for user-calibrated entries and for BASE-frame
  // extrinsics, where the field has no meaning. Downstream consumers (e.g.
  // runLocate) still compose TCP poses against the wrist by default;
  // mountBody is preserved so a future change can resolve non-wrist mounts
  // without re-deriving the parent from the camera-bot RPC.
  mountBody: zod.string().nullable().default(null),
});

export type ExtrinsicsCalibrationDataEntry = zod.infer<
  typeof ExtrinsicsCalibrationDataEntry
>;

export const IntrinsicsCalibrationData = zod.record(
  zod.string(),
  IntrinsicsCalibrationDataEntry,
);

export type IntrinsicsCalibrationData = zod.infer<
  typeof IntrinsicsCalibrationData
>;

export const ExtrinsicsCalibrationData = zod.record(
  zod.string(),
  ExtrinsicsCalibrationDataEntry,
);

export type ExtrinsicsCalibrationData = zod.infer<
  typeof ExtrinsicsCalibrationData
>;

export const ExtrinsicsKinematics = zod.object({
  extrinsics: CartesianPose,
  kind: ExtrinsicsType,
});

export type ExtrinsicsKinematics = zod.infer<typeof ExtrinsicsKinematics>;

// One cell of the capture-pose coverage grid. `filled` is true once a captured
// view populates the bucket keyed by `key` (e.g. a spatial/angular region).
export const CoverageBucket = zod.object({
  key: zod.string(),
  filled: zod.boolean(),
});

export type CoverageBucket = zod.infer<typeof CoverageBucket>;

// A per-view quality warning: `index` points at the offending calibration
// entry, `issue` classifies why it hurts the calibration.
export const QualityIssue = zod.object({
  index: zod.number(),
  issue: zod.enum(['too-few-corners', 'too-far', 'too-oblique', 'motion-blur']),
});

export type QualityIssue = zod.infer<typeof QualityIssue>;

// Guidance for the capture UI: whether to keep capturing, stop, or target a
// specific under-covered region, with human-readable `detail`.
export const CalibrationRecommendation = zod.object({
  kind: zod.enum(['keep-going', 'done', 'need-region']),
  detail: zod.string(),
});

export type CalibrationRecommendation = zod.infer<
  typeof CalibrationRecommendation
>;

// Result of assessing how well a set of captured views covers the calibration
// space. A view that failed to solve has no per-view error: the backend emits
// NaN, which becomes `null` once the result crosses the routine-runner JSON
// transport — so `perViewErrors` entries are `number | null` on the client.
// `reprojectionError` is null until enough views converge.
export const CalibrationCoverageResult = zod.object({
  coverageBuckets: CoverageBucket.array(),
  perViewErrors: zod.number().nullable().array(),
  reprojectionError: zod.number().nullable(),
  qualityIssues: QualityIssue.array(),
  converged: zod.boolean(),
  recommendation: CalibrationRecommendation,
});

export type CalibrationCoverageResult = zod.infer<
  typeof CalibrationCoverageResult
>;
